import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Checkbox from '@mui/material/Checkbox';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  order_by: 'vp.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  branch: [],
  id: 0,
};

const normalizeBranch = (values) =>
  values
    .filter((v) => v !== 'ALL')
    .map((v) => Number(v))
    .filter((v) => v > 0);

function RpStockLotAging() {
  const mod = 'rp_stock_lot_aging';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [cboBranch, setCboBranch] = useState([]);
  const [condition, setCondition] = useState([]);
  const [urlExcel, setUrlExcel] = useState('');

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'vp.id', label: t(`attr.image`), align: 'center', width: 40 },
    { id: 'vp.barcode', label: 'Barcode', align: 'left' },
    { id: 'vp.name', label: 'ชื่อสินค้า', align: 'left' },
    { id: 'lot', label: 'Lot', align: 'left' },
    { id: 'b.shortcode', label: 'สาขา', align: 'center' },
    { id: 'aging', label: 'Aging (วัน)', align: 'right' },
  ];

  const view = (data) => {
    const branch = normalizeBranch(data.branch);
    const payload = { ...data, branch };
    setFind(payload);
    closeShow();

    if (branch.length === 0) {
      setRecord([]);
      setPageTotal(0);
      setPageNo(1);
      setLoading(false);
      return;
    }

    setLoading(true);

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: payload.search,
      page: payload.page,
      per_page: payload.perPage,
      orderby: payload.order_by,
      ordersort: payload.order_sort,
      branch: payload.branch,
      id: payload.id,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const excel = () => {
    const params = new URLSearchParams();
    params.append('search', find.search);
    params.append('id', find.id);
    find.branch.forEach((branchId) => {
      params.append('branch[]', branchId);
    });

    window.open(`${urlExcel}/download?${params.toString()}`, '_self');
  };

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const renderLoading = () => (
    <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
      <CircularProgress color='inherit' />
    </Backdrop>
  );

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const renderSearch = () => (
    <Card sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <MDBox
            noValidate
            component='form'
            role='form'
            onSubmit={(e) => {
              e.preventDefault();
              view({ ...find, page: 1 });
            }}
          >
            <MDInput
              name='searchform'
              fullWidth
              value={find.search}
              type='search'
              onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                ),
                autoComplete: 'off',
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <MDInput
            select
            fullWidth
            SelectProps={{
              multiple: true,
              renderValue: (selected) => {
                if (selected.length === cboBranch.length) return 'ทั้งหมด';
                return cboBranch
                  .filter((item) => selected.includes(Number(item.id)))
                  .map((item) => item.name)
                  .join(', ');
              },
            }}
            value={find.branch}
            onChange={(e) => {
              const { value } = e.target;

              if (value.includes('ALL')) {
                const allSelected = find.branch.length === cboBranch.length;
                view({
                  ...find,
                  branch: allSelected ? [] : cboBranch.map((item) => Number(item.id)),
                  page: 1,
                });
              } else {
                view({ ...find, branch: normalizeBranch(value), page: 1 });
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>{t('menu.branch')} :</InputAdornment>
              ),
            }}
          >
            <MenuItem value='ALL'>
              <Checkbox checked={find.branch.length === cboBranch.length && cboBranch.length > 0} />
              ทั้งหมด
            </MenuItem>
            {cboBranch.map((item) => (
              <MenuItem key={item.id} value={Number(item.id)}>
                <Checkbox checked={find.branch.includes(Number(item.id))} />
                {item.name}
              </MenuItem>
            ))}
          </MDInput>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <MDInput
            fullWidth
            value={find.id}
            select
            onChange={(e) => view({ ...find, id: e.target.value, page: 1 })}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>{t('menu.condition')} :</InputAdornment>
              ),
            }}
          >
            {condition.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </MDInput>
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={3}>
          <MDBox display='flex' justifyContent='end' gap={1}>
            <MDButton variant='gradient' color='success' onClick={excel}>
              <Icon>table_view</Icon>&nbsp;Excel
            </MDButton>
          </MDBox>
        </Grid>
      </Grid>
    </Card>
  );

  const renderList = () => (
    <MDBox mb={7}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={3} md={2} lg={1}>
          <MDButton
            variant='contained'
            color='dark'
            size='large'
            fullWidth
            sx={{ pt: 1.6, pb: 1.6 }}
            href='/report_warehouse'
          >
            <Icon>content_paste_search</Icon>
          </MDButton>
        </Grid>
        <Grid item xs={12} sm={9} md={10} lg={11}>
          {renderSearch()}
        </Grid>
        <Grid item xs={12}>
          <TableContainer component={Paper} sx={{ minHeight: 400 }}>
            <Table aria-label='datatable table'>
              <TableHeader
                order={order}
                orderBy={orderBy}
                onRequestSort={handleSort}
                data={initHeader}
              />
              <TableBody>
                {record.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={initHeader.length} align='center'>
                      ไม่พบข้อมูล{find.branch.length === 0 ? ' กรุณาเลือกสาขา' : ''}
                    </TableCell>
                  </TableRow>
                ) : null}
                {record.map((row) => (
                  <TableRow key={`${row.barcode_id}-${row.branch_id}-${row.lot}`} hover>
                    <TableCell>{Func.imageDisplay(row)}</TableCell>
                    <TableCell className='nowrap'>{row.barcode}</TableCell>
                    <TableCell>
                      <MDBox className='nowrap'>{row.name}</MDBox>
                    </TableCell>
                    <TableCell className='nowrap'>{row.lot}</TableCell>
                    <TableCell align='center'>{row.shortcode}</TableCell>
                    <TableCell align='right' className='highlight-success'>
                      {Func.numberDisplay(row.aging)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid item xs={12}>
          <Card sx={{ p: 2 }}>
            <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
              <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                {t(`element.show_record`, { num: find.perPage })}&nbsp;
                <Icon>keyboard_arrow_down</Icon>
              </MDButton>
              {renderShow}
              <Pagination
                count={pageTotal}
                variant='outlined'
                color='secondary'
                showFirstButton
                showLastButton
                page={pageNo}
                onChange={(e, p) => view({ ...find, page: p })}
              />
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    FetchData('GET', `/${mod}`, app.token, { lang: app.lang }).then((result) => {
      if (result.status === 'ok') {
        setCboBranch(result.branch);
        setCondition(result.condition);
        setUrlExcel(result.excel);
        view({
          ...find,
          branch: result.branch.map((b) => Number(b.id)),
          id: 0,
        });
      } else {
        Func.exitApp();
      }
    });
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_stock`), t(`menu.rp_stock_lot_aging`)]} />
      {renderList()}
      {renderLoading()}
    </MDBox>
  );
}

export default RpStockLotAging;
